import React from 'react';
import PropTypes from 'prop-types';
import {
  Container,
  Nav,
  Row,
  TabContainer,
  TabContent,
  TabPane
} from 'react-bootstrap';
import add from 'date-fns/add';
import format from 'date-fns/format';
import { Swiper, SwiperSlide } from 'swiper/react';
import SwiperCore, { Navigation, Pagination, Scrollbar, A11y } from 'swiper';

import { CONTENT_PROPS } from '../shared/constants';

//#region /////// COMPONENTS ///////
import EPGDayItem from './components/EPGDayItem';
import EPGProgramList from './components/EPGProgramList';
import EPGProgramItem from './components/EPGProgramItem';
import ProgramItemLoader from './components/ProgramItemLoader';
import WithErrorBoundary from '../../HOCs/WithErrorBoundary';
//#endregion

import * as actions from './actions';
import * as hooks from './hooks';
import reducer, { INITIAL_STATE } from './reducer';
import './styles.scss';

SwiperCore.use([Navigation, Pagination, Scrollbar, A11y]);

function EPGVerticalList({
  content,
  daysAfter = 7,
  daysBefore = 2,
  daysMaxRange = 15,
  displayedItemsNum = 3,
  displayedItemHeightInPx = 150,
  imageMode = 'THUMB',
  initialSelectedDay = Date.now(),
  locale,
  onChangeDayCallback = (dayItem) => console.log('>> Selected dayItem:', dayItem),
  title = 'Title',
  unityToken,
}) {
  const [state, dispatch] = React.useReducer(reducer, {
    ...INITIAL_STATE,
    selectedDay: initialSelectedDay
  });
  const DAY_SCROLLER_REF = React.useRef(null);

  //#region //// HOOKS ////
  hooks.useContentEPGInfo(content, state.selectedDay, dispatch, unityToken);
  const { width: dayScrollerWidth } = hooks.useComponentDimensions(DAY_SCROLLER_REF);
  //#endregion

  const currentDateLocale = require(`date-fns/locale/${locale}`);
  const currentMonth = format(state.selectedDay, 'LLLL', { locale: currentDateLocale });
  const defaultActiveDay = format(state.selectedDay, 'yyyy-MM-dd');
  const tabContentHeight = (displayedItemHeightInPx + 20) * displayedItemsNum;

  const getDaysBeforeAndAfter = React.useCallback((date, numItems) => {
    const days = [];
    let i;

    for (i = -Math.floor(numItems / 2); i < Math.ceil(numItems / 2); i++) {
      const day = add(date, { days: i}),
        formattedDay = format(day, 'yyyy-MM-dd'),
        weekDayName =  format(day, 'EEE', { locale: currentDateLocale }),
        enabled = i >= -parseInt(daysBefore) && i <= parseInt(daysAfter);

      if(enabled)
        { days.push({
          date: formattedDay,
          dayName: weekDayName,
          epochDate: day,
          enabled
        }); }
    };

    return days;
  }, [currentDateLocale, daysAfter, daysBefore]);

  const handleOnSelectDay = (dayItem) => {
    onChangeDayCallback && onChangeDayCallback(dayItem);
    dispatch(actions.changeCalendarDay(dayItem.epochDate));
  };

  const daysTabList = getDaysBeforeAndAfter(state.selectedDay, daysMaxRange);

  // Create loading component list
  const renderLoadingComponent = () => {
    let loaderItemsCollection = [];

    for (let i = 0; i < displayedItemsNum; i++) {
      loaderItemsCollection.push(<ProgramItemLoader
        heigth={displayedItemHeightInPx}
        key={`program-item-loader-${i}`}
        viewBox={`0 0 ${dayScrollerWidth} ${displayedItemHeightInPx}`}
        preserveAspectRatio="xMinYMin meet"
        {...(dayScrollerWidth > 0 ? { width: dayScrollerWidth } : {}) }
      />);
    }

    return (
      <>
        { loaderItemsCollection.map(c => c) }
      </>
    );
  };

  return (
    <section className="tbx-epgVerticalList">
      <Container>
        <TabContainer id={`tbx-epgVerticalList-${content.id}`} defaultActiveKey={defaultActiveDay}>
          <Row>
            <h2 className="tbx-epgVerticalList__title">
            {title.trim().replace(/^\w/, (c) => c.toUpperCase())} <strong>{currentMonth.trim().replace(/^\w/, (c) => c.toUpperCase())}</strong>
            </h2>
          </Row>
          <Row>
            <Nav
              ref={DAY_SCROLLER_REF}
              className="tbx-epgVerticalList__navwrapper"
              variant="pills"
            >
              <Swiper
                className="tbx-epgVerticalList__navwrapper-list"
                navigation={true}
              >
              {
                daysTabList.length > 0 &&
                daysTabList.map((dayItem, index) => {
                  return (<Nav.Item key={`navitem-${dayItem.date}-${index}`}>
                    <SwiperSlide className="tbx-epgVerticalList__navwrapper-dayslide">
                    <Nav.Link
                      eventKey={dayItem.date}
                      onSelect={(e) => handleOnSelectDay(dayItem)}
                    >
                      <EPGDayItem {...dayItem} />
                    </Nav.Link>
                    </SwiperSlide>
                  </Nav.Item>)
                })
              }
              </Swiper>
            </Nav>
          </Row>
          <Row>
            <TabContent>
            {
              daysTabList.length > 0 &&
              daysTabList.map((dayItem, index) => {
                return (
                  <TabPane eventKey={dayItem.date} key={`tabpane-${dayItem.date}-${index}`}>
                    <EPGProgramList
                      epgItemCollection={state.epgData.items}
                      isLoading={state.isLoading}
                      listHeight={tabContentHeight}
                      loadingComponent={renderLoadingComponent()}
                      renderItem={(item, key) => {
                        return (
                          <EPGProgramItem
                            content={item}
                            itemHeight={displayedItemHeightInPx}
                            imageMode={imageMode}
                            key={`epgprogramitem-${dayItem.date}-${key}`}
                          />
                        );
                      }}
                    />
                  </TabPane>
                )
              })
            }
            </TabContent>
          </Row>
        </TabContainer>
      </Container>
    </section>
  );
};

EPGVerticalList.propTypes = {
  content: PropTypes.shape({
    ...CONTENT_PROPS,
    contentType: PropTypes.oneOf(['BROADCAST'])
  }).isRequired,
  daysAfter: PropTypes.number,
  daysBefore: PropTypes.number,
  daysMaxRange: PropTypes.number,
  displayedItemHeightInPx: PropTypes.number,
  displayedItemsNum: PropTypes.number,
  epgID: PropTypes.string,
  epgItems: PropTypes.array,
  imageMode: PropTypes.oneOf(['POSTER', 'THUMB']),
  initialSelectedDay: PropTypes.number,
  onChangeDayHandler: PropTypes.func,
  title: PropTypes.string,
  unityToken: PropTypes.object
};

export default WithErrorBoundary(EPGVerticalList);
